// @flow
import React, { Children, type Node } from 'react';
import classnames from 'classnames';

import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { Text } from 'Components/frameworks/text/Text.jsx';

import { themes } from './Notification.themes';

type Props = {
  children?: Node,
  className?: string,
  closeLabel?: string,
  color?: Colors,
  hasBorder?: boolean,
  hasClose?: boolean,
  hasShadow?: boolean,
  icon?: IconName,
  iconWidth?: string,
  isCentered?: boolean,
  onClose?: () => mixed,
  text?: string,
  theme?: string,
  title?: string,
  dataTestId?: string,
};

export function Notification(props: Props) {
  const {
    children,
    className,
    closeLabel,
    color = COLORS.BLUE,
    hasBorder,
    hasClose,
    hasShadow,
    icon = ICONS.LIGHTBULB,
    iconWidth = '1.875rem',
    isCentered,
    onClose,
    text,
    theme = 'default',
    title,
    dataTestId,
  } = props;

  if (!title && !text && !Children.count(children)) {
    return null;
  }

  const {
    thCloseButton,
    thCloseButtonLabel,
    thIcon,
    thInner,
    thMessage,
    thRoot,
    thSlot,
    thText,
    thTextTheme,
    thTitle,
  } = themes[theme]({
    color,
    hasBorder,
    hasShadow,
    isCentered,
  });

  return (
    <div className={classnames(className, thRoot)} data-testid={dataTestId}>
      {!!icon && (
        <div className={classnames('leading-none', thIcon)}>
          <Icon name={icon} isNotSquare width={iconWidth} />
        </div>
      )}
      <div className={thInner}>
        {(text || title) && (
          <div className={thMessage}>
            {!!title && !text && <p className={classnames('m-0', thTitle)}>{title}</p>}
            {!!title && !!text && <h3 className={classnames('reset-heading', thTitle)}>{title}</h3>}
            <Text className={thText} text={text} theme={thTextTheme} />
          </div>
        )}
        {Children.count(children) > 0 && <div className={thSlot}>{children}</div>}
      </div>
      {hasClose && (
        <button
          className={classnames('leading-none reset-button', thCloseButton)}
          onClick={onClose}
          type="button"
        >
          {!!closeLabel && <span className={thCloseButtonLabel}>{closeLabel}</span>}
          <Icon name={ICONS.CROSS} width="0.625rem" />
        </button>
      )}
    </div>
  );
}
